import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgDialog,
  IgTypography,
} from "@intelligems/ig-theme";
import { Grid } from "@mui/material";
import { usePostHistory } from "@src/api/histories/post-history";
import { Experience } from "@src/store/slices/experiences/types";
import {
  getDialogPropDetails,
  isDialogOpen,
  setDialog,
} from "@src/store/slices/global";
import { contactSupport } from "@src/utils/contactSupport";
import {
  allIntegrationGuides,
  contentTestQaChecklist,
  endingThemeTest,
  postPurchaseThemeTest,
  priceTestIntegrationGettingStarted,
  priceTestQaChecklist,
  shippingTestQaChecklist,
  shippingTestSetup,
} from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";
import { useIsMutating } from "@tanstack/react-query";

interface ConfirmIgChecklistProps {
  onExperienceStart: (
    experiment: Experience,
    confirmedIntegration?: boolean
  ) => void;
  experience: Experience;
  onAdminOverrideClick: (
    experience: Experience,
    confirmedIntegration?: boolean
  ) => void;
}
export const ConfirmIgChecklist = ({
  onExperienceStart,
  experience,
  onAdminOverrideClick,
}: ConfirmIgChecklistProps) => {
  const isOpen = useSelector(isDialogOpen("confirmIgChecklist"));
  const dialogProps = useSelector(getDialogPropDetails("confirmIgChecklist"));

  const dispatch = useDispatch();
  const postHistory = usePostHistory();
  const isPostHistoryMutating = useIsMutating({
    mutationKey: ["postHistory"],
  });
  const [confirmChecklist, setConfirmChecklist] = useImmer({
    integration: experience.type.includes("content"),
    qa: false,
    themeTestEnd: experience.type !== "content/theme",
    postPurchaseThemeTest: experience.type !== "content/theme",
  });
  const handleClose = () => {
    setConfirmChecklist({
      qa: false,
      integration: false,
      themeTestEnd: false,
      postPurchaseThemeTest: false,
    });
    dispatch(
      setDialog({
        dialog: "confirmIgChecklist",
        open: false,
      })
    );
  };

  const integrationWording = useMemo(() => {
    if (experience.type.includes("content")) {
      return { link: null, label: null };
    } else if (experience.type === "pricing") {
      return {
        label: "price test integration",
        link: priceTestIntegrationGettingStarted,
      };
    } else if (experience.type === "shipping") {
      return {
        label: "shipping test integration",
        link: shippingTestSetup,
      };
    } else {
      return {
        label: "offer test integration",
        link: allIntegrationGuides,
      };
    }
  }, [experience]);

  return (
    <IgDialog
      title={"Confirm QA"}
      handleClose={handleClose}
      showDialog={isOpen}
      dialogContentWidth={"470px"}
      dialogHeight={null}
      maxWidth={"lg"}
      hideActions={true}
      rawContent={
        <Grid
          textAlign={"start"}
          item
          container
          direction={"column"}
          rowSpacing={2}
          // columnSpacing={2}
        >
          {integrationWording?.label ? (
            <Grid
              // columnSpacing={2}
              item
              container
              direction={"row"}
              wrap={"nowrap"}
            >
              <Grid item>
                <IgCheckbox
                  disabled={Boolean(isPostHistoryMutating)}
                  sx={{ pt: 0 }}
                  onChange={(e) => {
                    setConfirmChecklist((draft) => {
                      draft.integration = e.target.checked;
                      return draft;
                    });
                  }}
                  checked={confirmChecklist.integration}
                />
              </Grid>
              <Grid item>
                <IgTypography>
                  Check here to confirm that the{" "}
                  <IgTypography
                    igStyle={"link"}
                    onClick={() => {
                      openNewTab(integrationWording.link);
                    }}
                  >
                    {integrationWording.label}{" "}
                  </IgTypography>
                  has been completed for your store.
                </IgTypography>
              </Grid>
            </Grid>
          ) : experience.type === "content/theme" ? (
            <Grid item container direction={"column"} rowSpacing={2}>
              <Grid item container direction={"row"} wrap={"nowrap"}>
                <Grid item>
                  <IgCheckbox
                    disabled={Boolean(isPostHistoryMutating)}
                    sx={{ pt: 0 }}
                    onChange={(e) => {
                      setConfirmChecklist((draft) => {
                        draft.themeTestEnd = e.target.checked;
                        return draft;
                      });
                    }}
                    checked={confirmChecklist.themeTestEnd}
                  />
                </Grid>
                <Grid item>
                  <IgTypography>
                    Check here to confirm you understand themes in theme tests{" "}
                    <IgTypography
                      igStyle={"link"}
                      onClick={() => {
                        openNewTab(endingThemeTest, "Ending a Theme Test");
                      }}
                    >
                      should not be deleted
                    </IgTypography>{" "}
                    for at least a month after a theme test has been ended.
                  </IgTypography>
                </Grid>
              </Grid>
              <Grid item container wrap={"nowrap"} direction={"row"}>
                <Grid item>
                  <IgCheckbox
                    disabled={Boolean(isPostHistoryMutating)}
                    sx={{ pt: 0 }}
                    onChange={(e) => {
                      setConfirmChecklist((draft) => {
                        draft.postPurchaseThemeTest = e.target.checked;
                        return draft;
                      });
                    }}
                    checked={confirmChecklist.postPurchaseThemeTest}
                  />
                </Grid>
                <Grid item>
                  <IgTypography>
                    Check here to confirm that you have added Intelligems
                    JavaScript to all themes included in your test, as well as
                    the Additional Scripts section for your Post-purchase page
                    in your checkout settings. See the help guide on completing
                    this{" "}
                    <IgTypography
                      igStyle={"link"}
                      onClick={() => {
                        openNewTab(
                          postPurchaseThemeTest,
                          "Post-purchase Theme Test"
                        );
                      }}
                    >
                      here.
                    </IgTypography>
                  </IgTypography>
                </Grid>
              </Grid>
            </Grid>
          ) : null}
          <Grid
            // columnSpacing={2}
            wrap={"nowrap"}
            item
            container
            direction={"row"}
          >
            <Grid item>
              <IgCheckbox
                disabled={Boolean(isPostHistoryMutating)}
                sx={{ pt: 0 }}
                onChange={(e) => {
                  setConfirmChecklist((draft) => {
                    draft.qa = e.target.checked;
                    return draft;
                  });
                }}
                checked={confirmChecklist.qa}
              />
            </Grid>
            <Grid item>
              <IgTypography>
                Check here to confirm that you have properly QA'd this test
                <IgTypography
                  igStyle={
                    experience.type !== "personalization/offer"
                      ? "link"
                      : undefined
                  }
                  onClick={() => {
                    if (experience.type !== "personalization/offer") {
                      openNewTab(
                        experience.type === "pricing"
                          ? priceTestQaChecklist
                          : experience.type === "shipping"
                            ? shippingTestQaChecklist
                            : contentTestQaChecklist
                      );
                    }
                  }}
                >
                  {experience.type !== "personalization/offer"
                    ? " (see checklist here)"
                    : ""}
                </IgTypography>
                .
              </IgTypography>
            </Grid>
          </Grid>
          <Grid item ml={1.6}>
            <IgTypography>
              Have questions?{" "}
              <IgTypography onClick={contactSupport} igStyle={"link"}>
                Reach out to support.
              </IgTypography>
            </IgTypography>
          </Grid>
          <Grid item container direction={"row"} justifyContent={"end"}>
            {!isPostHistoryMutating && (
              <Grid item>
                <IgButton color={"secondary"} onClick={handleClose}>
                  Cancel
                </IgButton>
              </Grid>
            )}
            <Grid item ml={3}>
              <IgButton
                disabled={
                  !confirmChecklist.qa ||
                  !confirmChecklist.integration ||
                  !confirmChecklist.themeTestEnd
                }
                loading={Boolean(isPostHistoryMutating)}
                onClick={() => {
                  postHistory.mutate(
                    {
                      experienceId: experience.id!,
                      action: "confirmIntegration",
                    },
                    {
                      onSuccess: () => {
                        setConfirmChecklist({
                          qa: false,
                          integration: false,
                          themeTestEnd: false,
                          postPurchaseThemeTest: false,
                        });

                        if (dialogProps.isSchedule) {
                          dispatch(
                            setDialog({
                              dialog: "scheduleTest",
                              open: true,
                              props: { experience },
                            })
                          );
                          handleClose();
                        } else {
                          if (dialogProps?.isAdminOverride) {
                            onAdminOverrideClick(experience);
                          } else {
                            onExperienceStart(experience);
                          }

                          handleClose();
                        }
                      },
                    }
                  );
                }}
                variant={"contained"}
              >
                Confirm
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      }
      yesButtonText={"Confirm"}
    />
  );
};
